import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NavigationExtras, Router } from '@angular/router';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { ModalController } from '@ionic/angular';
import { deleteField, doc, getFirestore, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ComponentsService } from '../components/components.service';

/**
 * URL raiz de la app, capturada al evaluar el bundle — antes de que el router toque la barra de
 * direcciones.
 *
 * Importa el momento: el build de app (`build:app:*`) usa `--base-href ./` y se sirve desde
 * `https://cdn.quantopos.com/apps/abc/`, asi que `document.baseURI` solo resuelve a la raiz mientras
 * la URL siga siendo la de arranque; ya dentro de una ruta profunda apuntaria al directorio
 * equivocado. El build web usa `--base-href /` y da la raiz del dominio siempre.
 *
 * Se recarga siempre contra esta raiz y nunca contra una ruta profunda: la raiz es lo unico que
 * ambos despliegues (nginx web y CDN) garantizan servir sin depender de un fallback SPA.
 */
const APP_ROOT_URL = document.baseURI;

/** Ruta a abrir despues de un reload de cambio de cuenta. Vive solo en esta pestaña. */
const POST_RELOAD_ROUTE_KEY = 'quanto:post_reload_route';

/** Permiso de device para ver costos (ver `canViewCosts`). */
export const COSTS_READ_PERMISSION = 'inventory.costs.read';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {

    private initialized: boolean = false;

    public isAccountAvailable: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private authenticatedSubject = new BehaviorSubject<boolean>(false);

    get isAuthenticated(): Observable<boolean> {
        return this.authenticatedSubject.asObservable();
    }

    private accountUnsub?: () => void;
    private deviceUnsub?: () => void;

    private readonly _countState$ = new BehaviorSubject<{
        hasActiveCount: boolean;
        counting_key: string | null;
    }>({
        hasActiveCount: false,
        counting_key: null
    });

    /**
     * true si esta area todavia debe hacer su inventario inicial (y no lo marco como
     * "saltar") y todavia no lo termino — la escribe InitialInventoryGuard cada vez que
     * resuelve el chequeo. Distinto de `countState$.hasActiveCount`, que solo es true una
     * vez que ya existe una sesion de conteo activa (se presiono "Iniciar Conteo"): este
     * flag cubre tambien el momento previo, cuando el guard ya redirigio a la pantalla
     * pero la sesion todavia no arranca — para que el sidebar se vea bloqueado igual.
     *
     * Arranca en `true` (bloqueado) a proposito: el guard resuelve de forma asincrona
     * (lectura a Firestore) y el sidebar se monta antes de que termine, asi que si el
     * default fuera `false` habria un destello del sidebar habilitado justo al entrar.
     * Es preferible que el destello (si lo hay) sea hacia "bloqueado", nunca al reves.
     */
    public requiresInitialCount$ = new BehaviorSubject<boolean>(true);

    account_key: any;
    device_key: any;
    location_key: any;
    area_key: any;
    mode: any;
    device: any;
    license: any;
    location: any;
    area: any;
    account: any;
    uid: any;
    user: any;
    logged_token: boolean = false;
    user$ = this.angularAuth.authState;
    private destroy$ = new Subject<void>();

    constructor(
        public database: AngularFirestore,
        private router: Router,
        public db: AngularFirestore,
        private angularAuth: AngularFireAuth,
        public components: ComponentsService,
        private http: HttpClient,
        private modalCtrl: ModalController,
    ) {
    }

    readonly countState$ = this._countState$.asObservable();

    async waitForAccount(): Promise<void> {
        if (this.account) return; // ya está
        await firstValueFrom(this.isAccountAvailable.pipe(
            filter(v => v === true),
            take(1)
        ));
    }


    async init(): Promise<void> {
        return new Promise(async (resolve) => {
            if (this.initialized) return;

            const status = await Network.getStatus().catch(() => ({ connected: false }));
            if (!status.connected) {
                setTimeout(() => this.init(), 5000);
                return;
            }

            this.angularAuth.authState.subscribe(async (firebaseUser) => {
                if (firebaseUser) {

                    this.uid = firebaseUser.uid;
                    await this.checkUser();
                    this.authenticatedSubject.next(true);

                    await this.loadToken().then(() => {
                        this.initialized = true;
                        resolve();
                    })
                } else {
                    this.authenticatedSubject.next(false);
                    this.initialized = true;
                    resolve();
                }
            });
        });
    }

    async loadToken() {
        const keys = ['type', 'account_key', 'device_key', 'location_key', 'area_key'];
        const stored = await Promise.all(keys.map(key => Preferences.get({ key })));
        const [type, acc, dev, loc, area] = stored.map(d => d.value);

        if (type === 'abc' && acc && dev) {
            await this.setAbcContext({
                account_key: acc,
                device_key: dev,
                location_key: loc || null,
                area_key: area || null,
            });
            this.checkDevice();
            this.checkLocation();
            this.checkArea();
            this.authenticatedSubject.next(true);
            return;
        }

        if (type === 'admin' && !acc) {
            this.mode = 'admin';
            this.account_key = null;
            this.authenticatedSubject.next(true); // deja pasar al /accounts
            return;
        }

        if (type === 'admin' && acc) {
            await this.setAdminContext(acc);
            this.authenticatedSubject.next(true);
            return;
        }

        // Sin contexto válido
        this.authenticatedSubject.next(false);
    }

    public async setAdminContext(account_key: string) {
        this.mode = 'admin';
        this.account_key = account_key;
        this.device_key = this.location_key = this.area_key = null;

        await Preferences.set({ key: 'type', value: 'admin' });
        await Preferences.set({ key: 'account_key', value: account_key });
        await Preferences.remove({ key: 'device_key' });
        await Preferences.remove({ key: 'location_key' });
        await Preferences.remove({ key: 'area_key' });

        this.attachAccountListener(account_key);

    }

    normalizeUser(user: any) {
        if (!user) return null;

        return {
            $key: user?.$key || user?.uid || null,
            uid: user?.uid || user?.$key || null,
            name: user?.name || '',
            lastname: user?.lastname || '',
            email: user?.email || '',
            type: user?.type || null,
        };
    }

    get user_resume() {
        return this.normalizeUser(this.user);
    }

    /**
     * Permisos del usuario en el area de la sesion. Misma regla que aplica la directiva
     * `*appCan` (que delega aqui): sin usuario o sin area, no hay permiso.
     *
     * Existe ademas de la directiva porque ocultar un boton no alcanza cuando la accion tambien
     * se puede disparar desde codigo (p.ej. continuar un borrador abre el modal de creacion).
     */
    can(required: string | string[], mode: 'any' | 'all' = 'any'): boolean {
        if (!this.user || !this.area_key) return false;

        const granted: string[] = this.user.permissions?.[this.area_key] ?? [];
        const permissions = Array.isArray(required) ? required : [required];

        return mode === 'all'
            ? permissions.every((permission) => granted.includes(permission))
            : permissions.some((permission) => granted.includes(permission));
    }

    /**
     * Puede ver costos (WAC, historial de costo, valor de producciones y transferencias). Admin ABC
     * siempre; en el device hace falta `inventory.costs.read` en el area de la sesion. Solo
     * controla lo que se MUESTRA: los calculos y lo que se guarda no cambian.
     */
    canViewCosts(): boolean {
        return this.mode === 'admin' || this.can(COSTS_READ_PERMISSION);
    }

    private async setAbcContext(opts: { account_key: string; device_key: string; location_key?: string | null; area_key?: string | null; }) {
        this.mode = 'abc';
        this.account_key = opts.account_key;
        this.device_key = opts.device_key;
        this.location_key = opts.location_key ?? null;
        this.area_key = opts.area_key ?? null;

        await Preferences.set({ key: 'type', value: 'abc' });
        await Preferences.set({ key: 'account_key', value: opts.account_key });
        await Preferences.set({ key: 'device_key', value: opts.device_key });
        await Preferences.set({ key: 'location_key', value: this.location_key ?? '' });
        await Preferences.set({ key: 'area_key', value: this.area_key ?? '' });

        this.attachAccountListener(opts.account_key);
        this.attachDeviceListener(opts.device_key);

    }

    private attachAccountListener(account_id: string) {
        // Limpia anterior
        if (this.accountUnsub) { this.accountUnsub(); this.accountUnsub = undefined; }

        this.account = null;
        this.isAccountAvailable.next(false);

        const docRef = this.db.collection('accounts_abc').doc(account_id).ref;

        this.accountUnsub = docRef.onSnapshot(async (snapshot: any) => {
            if (!snapshot.exists) {
                this.account = null;
                this.isAccountAvailable.next(false);
                return;
            }

            this.account = { $key: snapshot.id, ...snapshot.data() };

            if (this.mode == 'admin') {
                try {
                    const userDoc: any = await this.db
                        .collection(`accounts_abc/${account_id}/users`)
                        .doc(this.uid).ref.get();
                    if (userDoc.exists) {
                        this.user = { $key: userDoc.id, ...userDoc.data() };
                    }
                } catch (e) {
                    console.warn('No se pudo leer user subdoc:', e);
                }
            }

            this.isAccountAvailable.next(true);
        }, (err: any) => {
            console.error('Error snapshot account:', err);
            this.isAccountAvailable.next(false);
        });
    }

    // Fuerza el cierre de sesión si el device deja de ser la sesión activa
    // (admin lo desactivó, lo reasignó, o fue reclamado por otro device con el mismo código).
    private attachDeviceListener(device_key: string) {
        if (this.deviceUnsub) { this.deviceUnsub(); this.deviceUnsub = undefined; }
        if (!device_key) return;

        const docRef = this.db.collection('devices_abc').doc(device_key).ref;

        this.deviceUnsub = docRef.onSnapshot((snapshot: any) => {
            if (this.mode !== 'abc' || !this.uid) return;

            const data = snapshot.data();
            const stillActive = !!snapshot.exists && data?.activeUID === this.uid;

            if (!stillActive) {
                // No revertir 'available' acá: si un admin desactivó el device, debe
                // quedar bloqueado; si otro device reclamó el mismo código, ese device
                // ya es el dueño legítimo del slot.
                this.logOut({ releaseDevice: false });
            }
        }, (err: any) => {
            console.error('Error snapshot device:', err);
        });
    }

    async registerDevice(code: any): Promise<void> {

        try {
            const anonUser: any = await this.angularAuth.signInAnonymously();

            const deviceId = anonUser.user.uid;

            const snapshots = await this.db.collection('devices_abc').ref
                .where('code', '==', code)
                .get();

            if (snapshots.empty) {
                return await this.logoutAndReject("Device not found");
            }

            const deviceKey = snapshots.docs[0].id;
            const capacitorDeviceId = await Device.getId();

            let device: any;
            try {
                device = await this.db.firestore.runTransaction(async (transaction: any) => {
                    const globalRef = this.db.firestore.collection('devices_abc').doc(deviceKey);
                    const snapshot = await transaction.get(globalRef);

                    if (!snapshot.exists) {
                        throw new Error('Device not found');
                    }

                    const data: any = { ...(snapshot.data() || {}), $key: snapshot.id };

                    // Re-leer y validar 'available' dentro de la propia transacción evita que
                    // dos devices reclamen el mismo código casi simultáneamente (condición de carrera).
                    if (!data.available) {
                        throw new Error('Device not available');
                    }

                    const accountRef = this.db.firestore
                        .collection(`accounts_abc/${data.account_key}/devices`)
                        .doc(deviceKey);

                    const update = {
                        available: false,
                        device_id: capacitorDeviceId.identifier,
                        updated_at: serverTimestamp(),
                    };

                    transaction.update(globalRef, { ...update, activeUID: deviceId });
                    transaction.update(accountRef, update);

                    return data;
                });
            } catch (txError: any) {
                return await this.logoutAndReject(txError?.message === 'Device not available' ? 'Device not available' : 'Device not found');
            }

            await this.saveDevicePreferences(device);

            this.authenticatedSubject.next(true);
            await this.loadToken();

        } catch (error) {
            console.error("Error in registerDevice:", error);
            await this.logoutAndReject("Unexpected error");
        }
    }

    async loginAdminUser(email: any, password: any) {
        try {
            const cred = await this.angularAuth.signInWithEmailAndPassword(email, password);
            const uid = cred.user?.uid;
            if (!uid) throw new Error('UID inválido');
            this.uid = uid;

            await Preferences.set({ key: 'type', value: 'admin' });
            await Preferences.remove({ key: 'account_key' });
            await Preferences.remove({ key: 'device_key' });
            await Preferences.remove({ key: 'location_key' });
            await Preferences.remove({ key: 'area_key' });

            // Cargar datos de la cuenta
            this.authenticatedSubject.next(true);
            await this.router.navigateByUrl('admin/accounts', { replaceUrl: true });
            return true;
        } catch (error) {
            console.error('Error logging in admin:', error);
            this.authenticatedSubject.next(false);
            return false;
        }
    }

    async checkDevice() {
        try {
            const snapshot: any = await this.db.collection(`accounts_abc/${this.account_key}/devices`).ref.doc(this.device_key).get();
            this.device = { ...snapshot.data(), $key: snapshot.id };
        } catch (err) {
            console.error(err);
        }
    }

    async checkLocation() {
        try {
            const locationSnapshot: any = await this.db.collection(`accounts_abc/${this.account_key}/locations`).ref.doc(this.location_key).get();
            this.location = { ...locationSnapshot.data(), $key: locationSnapshot.id };
        } catch (error) {
            console.error(error);
        }
    }

    async checkArea() {
        try {
            const areaSnapshot: any = await this.db.collection(`accounts_abc/${this.account_key}/areas`).ref.doc(this.area_key).get();
            this.area = { ...areaSnapshot.data(), $key: areaSnapshot.id };
        } catch (err) {
            console.error(err);
        }
    }

    async checkUser() {
        try {
            const userSnapshot: any = await this.db.collection(`users`).ref.doc(this.uid).get();
            this.user = { ...userSnapshot.data(), $key: userSnapshot.id };

        } catch (error) {
            console.error(error);
        }
    }

    private async saveDevicePreferences(device: any): Promise<void> {
        const preferences = [
            { key: 'type', value: 'abc' },
            { key: 'account_key', value: device.account_key },
            { key: 'device_key', value: device.$key },
            { key: 'location_key', value: device.location_key },
            { key: 'area_key', value: device.area_key },
        ];

        await Promise.all(preferences.map(pref => Preferences.set(pref)));
    }

    private async logoutAndReject(message: string): Promise<void> {
        const user = await this.angularAuth.currentUser;
        if (user && user.isAnonymous) {
            await user.delete();
            await this.angularAuth.signOut();
        }
        this.authenticatedSubject.next(false);
        throw new Error(message);
    }

    logout() {
        this.angularAuth.signOut().then(() => {
            window.location.reload();
        })
    }

    /**
     * Recarga la app completa desde su raiz.
     *
     * Es la unica forma de garantizar que no sobreviva NADA de la cuenta anterior. La app tiene
     * multiples singletons con cache scopeado a la cuenta (`ReportsService` con ubicaciones, areas,
     * categorias, costos y presentaciones — ademas memoizadas con promesas que impiden recargar —,
     * `NotificationsService` con su listener de Firestore vivo, `DashboardDetailService`,
     * `PrintService`, `ComponentsService`) y hasta los propios campos de este servicio. Limpiarlos uno
     * por uno obliga a acordarse de cada servicio nuevo que aparezca; recargar no.
     *
     * Se usa `replace()` y no `href`: ademas de recargar siempre, saca la pantalla anterior del
     * historial, para que el boton Atras no devuelva a una vista renderizada con la cuenta vieja.
     */
    reloadApp(): void {
        window.location.replace(APP_ROOT_URL);
    }

    /** Deja anotada la ruta a abrir despues del reload (se consume una sola vez). */
    setPostReloadRoute(route: string): void {
        try {
            sessionStorage.setItem(POST_RELOAD_ROUTE_KEY, route);
        } catch {
            // sessionStorage puede fallar (modo privado); sin marca simplemente se cae al
            // destino por defecto, que es el selector de cuentas.
        }
    }

    consumePostReloadRoute(): string | null {
        try {
            const route = sessionStorage.getItem(POST_RELOAD_ROUTE_KEY);
            if (route) sessionStorage.removeItem(POST_RELOAD_ROUTE_KEY);
            return route;
        } catch {
            return null;
        }
    }

    async lockUser() {
        const modals = Array.from(document.getElementsByTagName("ion-modal"));
        const popovers = Array.from(document.getElementsByTagName("ion-popover"));
        const alerts = Array.from(document.getElementsByTagName("ion-alert"));

        for (const modal of modals) await modal.dismiss();
        for (const popover of popovers) await popover.dismiss();
        for (const alert of alerts) await alert.dismiss();

        Preferences.remove({ key: 'uid' }).then(data => {
            this.uid = '';
            this.router.navigate(['/code'], { replaceUrl: true, clearHistory: true, } as NavigationExtras);
        })
    }

    async logOut(opts?: { skipNavigate?: boolean; releaseDevice?: boolean }): Promise<void> {

        try {
            // 1) Limpiar prefs en paralelo
            await Promise.all([
                Preferences.remove({ key: 'type' }),
                Preferences.remove({ key: 'account_key' }),
                Preferences.remove({ key: 'device_key' }),
                Preferences.remove({ key: 'location_key' }),
                Preferences.remove({ key: 'area_key' }),
            ]);

            if (this.deviceUnsub) { this.deviceUnsub(); this.deviceUnsub = undefined; }
            if (this.accountUnsub) { this.accountUnsub(); this.accountUnsub = undefined; }

            this.authenticatedSubject.next(false);
            // Vuelve al default "bloqueado": la proxima sesion (mismo u otro device/area)
            // debe esperar a que InitialInventoryGuard resuelva de nuevo, no heredar el
            // valor de la sesion anterior.
            this.requiresInitialCount$.next(true);

            // 2) Liberar device SOLO si tenés claves definidas, aplica a tu modo actual (device ABC)
            // y el logout es voluntario. Si fue forzado por una desactivación remota
            // (releaseDevice: false), no se debe revertir el 'available: false' que puso el admin.
            const batch = this.db.firestore.batch();
            const wasAbcDevice = this.mode === 'abc' && !!this.device_key && opts?.releaseDevice !== false;

            if (wasAbcDevice) {
                batch.update(this.db.firestore.collection('devices_abc').doc(this.device_key), {
                    available: true,
                    device_id: '',
                    activeUID: deleteField()
                });
            }

            if (wasAbcDevice && this.account_key) {
                batch.update(
                    this.db.firestore
                        .collection(`accounts_abc/${this.account_key}/devices`)
                        .doc(this.device_key),
                    {
                        available: true,
                        device_id: '',
                    }
                );
            }

            // Si no había nada que actualizar, Firestore falla un commit vacío.
            // Solo comiteá si agregaste updates:
            // (truco simple: llevá un flag o contá con try/catch y omite commit si no hubo update)
            try {
                // Si no hubo updates, este commit podría fallar: capturalo silenciosamente
                await batch.commit();
            } catch (e) {
                // No pasa nada si no había updates; log opcional:
                console.warn('Batch commit skipped or failed:', e);
            }

            // 3) Cerrar sesión Firebase
            const user = await this.angularAuth.currentUser;

            if (user?.isAnonymous) {
                try { await user.delete(); } catch (e) { console.error('No se pudo eliminar anónimo:', e); }
            }

            await this.angularAuth.signOut();

        } catch (err) {
            console.error('Error en logOut():', err);
            // Aun con error, intentá llevar al login
        } finally {
            // 4) Salí SIEMPRE al login, recargando la app entera.
            //
            // Antes esto navegaba con el router, que no destruye los singletons: la cuenta anterior
            // quedaba viva en memoria (ubicaciones, areas, categorias, listener de notificaciones,
            // y hasta `this.account_key`), y recien se limpiaba si el usuario recargaba a mano.
            //
            // `skipNavigate` se respeta igual: AutoLoginGuard lo usa para cerrar sesion mientras
            // resuelve una ruta y navegar el mismo. Recargar ahi pelearia con el router.
            if (!opts?.skipNavigate) {
                this.reloadApp();
            }
        }
    }

    // Cambia 'available' del device (activar/desactivar desde el admin). Independientemente
    // del valor destino, si hay una sesión activa (activeUID) se revoca: el listener del
    // device (attachDeviceListener) detecta la limpieza de activeUID y cierra sesión de
    // inmediato en el device físico. Antes, desactivar (value=false) dejaba la sesión viva.
    async deleteAnonymousUser(value: any, device_key: any): Promise<boolean> {
        const snapshot: any = await this.db.collection('devices_abc').doc(device_key).ref.get();
        const device: any = { ...(snapshot.data() || {}), $key: snapshot.id };

        if (device.activeUID) {
            const postData = {
                device: device_key,
                account: this.account,
                admin: this.uid,
                uid: device.activeUID
            };

            try {
                await firstValueFrom(this.http.post(`${environment.api}/remove_anonymous`, postData));
            } catch (err) {
                console.warn('No se pudo revocar la sesión anónima del device:', err);
            }
        }

        const batch = this.db.firestore.batch();

        batch.update(this.db.firestore.collection(`accounts_abc/${this.account.$key}/devices`).doc(device_key), {
            available: value,
            device_id: '',
            activeUID: deleteField()
        });

        batch.update(this.db.firestore.collection(`devices_abc`).doc(device_key), {
            available: value,
            device_id: '',
            activeUID: deleteField()
        });

        await batch.commit();
        return true;
    }

    // Elimina un device (desde admin/devices). Si tiene una sesión anónima activa
    // (activeUID) se revoca primero igual que en deleteAnonymousUser, para no dejar
    // un usuario anónimo huérfano con sesión viva en el device físico. Luego borra
    // el doc en ambas colecciones espejo: accounts_abc/{account}/devices y devices_abc.
    async deleteDevice(device_key: any): Promise<boolean> {
        const snapshot: any = await this.db.collection('devices_abc').doc(device_key).ref.get();
        const device: any = { ...(snapshot.data() || {}), $key: snapshot.id };

        if (device.activeUID) {
            const postData = {
                device: device_key,
                account: this.account,
                admin: this.uid,
                uid: device.activeUID
            };

            try {
                await firstValueFrom(this.http.post(`${environment.api}/remove_anonymous`, postData));
            } catch (err) {
                console.warn('No se pudo revocar la sesión anónima del device:', err);
            }
        }

        const batch = this.db.firestore.batch();

        batch.delete(this.db.firestore.collection(`accounts_abc/${this.account.$key}/devices`).doc(device_key));
        batch.delete(this.db.firestore.collection(`devices_abc`).doc(device_key));

        await batch.commit();
        return true;
    }

    checkCode(code: any, message: string = 'Ingresando a Quanto POS...') {
        return new Promise((resolve, reject) => {
            this.components.showLoader(message, 'dark').then(() => {
                let real_code = code.slice(0, 4);
                this.db.collection(`accounts_abc/${this.account_key}/users`).ref
                    .where('code', '==', real_code)
                    .get()
                    .then((snapshots: any) => {

                        if (snapshots.empty) {
                            reject(false);
                        } else {
                            snapshots.forEach((element: any) => {
                                this.user = element.data();
                                this.user.$key = element.id;
                                this.uid = element.id;
                                this.logged_token = true;
                                resolve(true);
                                this.inventoryCountingListener();
                            });
                        }
                    }, err => {
                        console.error(err);
                        reject();
                    });
            })
        });
    }

    async checkUserCode(code: string): Promise<{ user: any } | false> {
        try {
            const real_code = code.slice(0, 4);
            const snapshots: any = await this.db
                .collection(`accounts_abc/${this.account_key}/users`)
                .ref.where('code', '==', real_code)
                .get();

            if (snapshots.empty) return false;

            let userData: any = null;
            snapshots.forEach((element: any) => {
                userData = element.data();
                userData.$key = element.id;
            });

            return { user: userData };
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    async createAccount(form_data: any): Promise<string> {
        try {
            const user_db: any = await this.angularAuth.createUserWithEmailAndPassword(form_data.email, form_data.password);
            this.uid = user_db.user?.uid;
            if (!this.uid) throw new Error('No se recibió UID del usuario');

            await this.checkUser();
            this.authenticatedSubject.next(true);

            this.user = {
                name: form_data.user_name,
                lastname: form_data.user_lastname,
                email: form_data.email,
            };

            const account_id = await this.setupNewAccount(form_data);
            this.setAdminContext(account_id);

            return account_id;
        } catch (err) {
            this.authenticatedSubject.next(false);
            throw err;
        }
    }

    async setupNewAccount(account_data: any): Promise<any> {

        try {
            const batch = this.db.firestore.batch();

            const accountRef = this.db.firestore.collection('accounts_abc').doc();

            batch.set(accountRef, {
                name: account_data.name,
                phone: account_data.phone,
                country: account_data.country,
                full_country: account_data.full_country,
                email: account_data.email,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                owner: this.uid,
                active: true,
                admins: [this.uid],
                currency: {
                    symbol: '$'
                }
            });

            const userRef = this.db.firestore.collection(`accounts_abc/${accountRef.id}/users`).doc(this.uid);

            let date = new Date();
            let renewal_date = new Date(date.setMonth(date.getMonth() + 1));
            const code = Math.floor(1000 + Math.random() * 9000);

            batch.set(this.db.firestore.collection(`users`).doc(this.uid), {
                email: account_data.email,
                name: account_data.user_name,
                lastname: account_data.user_lastname,
                phone: account_data.phone,
                creation_date: serverTimestamp(),
            });
            batch.set(userRef, {
                uid: this.uid,
                name: this.user.name,
                lastname: this.user.lastname,
                email: this.user.email,
                type: 'owner',
                roles: ['owner'],
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                active: true,
                renewal_date: renewal_date,
                code: String(code)
            });

            await batch.commit();

            return accountRef.id;

        } catch (err) {
            console.error(err);
            throw err;
        }
    }


    private inventoryCountingListener() {
        const ref = this.db.collection(
            `accounts_abc/${this.account_key}/inventory_counting`,
            (ref) =>
                ref
                    .where('area_key', '==', this.area_key)
                    .where('active', '==', true)
                    .limit(1)
        );

        const sub = ref.snapshotChanges().subscribe((snapshot) => {
            const hasActiveCount = snapshot.length === 1;

            const countingId = hasActiveCount ? snapshot[0].payload.doc.id : null;

            // El doc de conteo se reescribe en CADA guardado de borrador (ver onQuantityChange
            // en init-counting.page.ts), y Firestore ademas emite dos veces por escritura: la
            // optimista local y la confirmada por el servidor. Emitir siempre hacia countState$
            // hacia que la pagina recargara la lista de materiales encima de lo que el usuario
            // estaba escribiendo, borrandole las cantidades y el toggle "No contar". Solo se
            // emite cuando el estado cambia de verdad (hay conteo activo / cual es).
            const current = this._countState$.value;
            const changed = !current
                || current.hasActiveCount !== hasActiveCount
                || current.counting_key !== countingId;

            if (changed) {
                this._countState$.next({
                    hasActiveCount,
                    counting_key: countingId,
                });
            }

            const currentUrl = this.router.url.split('?')[0];
            const countingUrl = '/abc/inventory-counting/init-counting';

            if (hasActiveCount && currentUrl !== countingUrl) {
                this.router.navigateByUrl(countingUrl);
            } else if (!hasActiveCount && currentUrl === countingUrl) {
                this.router.navigateByUrl('/abc/inventory-counting/counting-summary');
            }
        });

        this.destroy$.pipe(take(1)).subscribe(() => sub.unsubscribe());
    }


    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get countState(): any {
        return this._countState$.value;
    }

    startCounting(key: string) {
        this._countState$.next({ hasActiveCount: true, counting_key: key });
    }

    stopCounting() {
        this._countState$.next({ hasActiveCount: false, counting_key: null });
    }
}
